/**
 * PageActionProvider
 *
 * Context wrapper that computes the PageActionContext for the current
 * page and provides it to PageToolbar and CommandPalette.
 *
 * Each page wraps its content in <PageActionProvider> to declare:
 *   - pageId (the route pattern the page occupies)
 *   - entityType / entityId (optional entity focus)
 *   - activePluginId (optional plugin scope)
 *
 * Child action components consume the context to filter, render,
 * and invoke the appropriate page actions from the PageActionRegistry.
 *
 * Performance:
 *   - Memoized context value prevents re-render cascades
 *   - Stable reference unless actual values change
 *   - Children are not re-rendered when context is unchanged
 *
 * No domain logic — pure infrastructure.
 */

import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { useDeviceContext } from '../../providers/DeviceTypeProvider';
import type { PageActionContext } from '../../types/page-actions';
import { useActivePageContextStore } from '../../plugins/registries/active-page-context-store';

// ── Context ─────────────────────────────────────────────────────

const PageActionCtx = createContext<PageActionContext>({
  pageId: '/',
  deviceType: 'desktop',
  isMobile: false,
});

// ── Provider Props ──────────────────────────────────────────────

export interface PageActionProviderProps {
  /** The current page identifier, e.g. "/entities" or "/entities/:entityId" */
  pageId: string;
  /** Entity type on this page, if any (e.g. "document") */
  entityType?: string;
  /** Entity ID on this page, if any */
  entityId?: string;
  /** Plugin ID that owns this page */
  activePluginId?: string;
  /** Additional context data (forwarded to action handlers) */
  extra?: Record<string, unknown>;
  children: React.ReactNode;
}

/**
 * Wraps a page and provides the computed PageActionContext to
 * toolbar/FAB/command palette children.
 *
 * @example
 * ```tsx
 * <PageActionProvider pageId="/entities" entityType="document" entityId={entityId}>
 *   <PageToolbar />
 *   <CommandPalette />
 *   {children}
 * </PageActionProvider>
 * ```
 */
export function PageActionProvider({
  pageId,
  entityType,
  entityId,
  activePluginId,
  extra,
  children,
}: PageActionProviderProps) {
  const { deviceType, isMobile } = useDeviceContext();

  // Stable reference for extra object — only update if contents change
  const extraRef = useRef(extra);
  const extraJson = JSON.stringify(extra);
  if (JSON.stringify(extraRef.current) !== extraJson) {
    extraRef.current = extra;
  }

  const context = useMemo<PageActionContext>(
    () => ({
      pageId,
      entityType,
      entityId,
      deviceType,
      isMobile,
      activePluginId,
      extra: extraRef.current,
    }),
    [pageId, entityType, entityId, deviceType, isMobile, activePluginId, extraJson],
  );

  // Mirror into the global store so app-root siblings (e.g. the assistant
  // overlay) can read "what's on screen" without being descendants of the
  // page tree.
  useEffect(() => {
    useActivePageContextStore.getState().setContext(context);
  }, [context]);

  return (
    <PageActionCtx.Provider value={context}>
      {children}
    </PageActionCtx.Provider>
  );
}

// ── Consumer hook ───────────────────────────────────────────────

/**
 * Access the current PageActionContext from the nearest PageActionProvider.
 *
 * Used by PageToolbar and CommandPalette to determine
 * which actions to render and how.
 */
export function usePageActionContext(): PageActionContext {
  return useContext(PageActionCtx);
}
